/**
 * Page state for the Premium Selection section.
 *
 * Model (jun-19): the premium walk is computed entirely on the BACKEND.
 * The FE no longer derives any total — it reads them off the quote
 * response (GET `/v2/submissions/{id}/quote/`). Flow: user edits inputs →
 * Save PUTs the quote → backend recomputes → re-GET → the totals refresh.
 * Until a quote is loaded `derived` is null and every card renders "—".
 *
 * The editable inputs still live here and still map via `fromWireQuote`;
 * only the source of the TOTALS changed (was a synchronous FE derive off
 * Schedule + Loss Runs refs — now the quote wire).
 *
 * `commit` / `setAndCommit` / `pending` are kept as no-op / pass-through
 * shims so the card components don't need to change.
 */
import { startTransition, useCallback, useRef, useState } from "react";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../domain/pricing";
import { defaultPremiumSelectionInputs } from "../../../domain/pricing";

export type InputKey = keyof PremiumSelectionInputs;
export type DerivedKey = keyof PremiumSelectionDerived;

/** No field is ever "pending" now — totals come with the quote load.
 *  Kept as a stable type so the cards' `pending` prop still type-checks. */
export type PendingMap = Partial<Record<DerivedKey, true>>;
const NO_PENDING: PendingMap = {};

export interface UsePremiumSelection {
    inputs: PremiumSelectionInputs;
    /** Update a field. Totals don't change until the next quote load. */
    setInput: (key: InputKey, value: string) => void;
    /** Update + (legacy) commit. Same as `setInput` now. */
    setAndCommit: (key: InputKey, value: string) => void;
    /** Adopt a server value ONLY if the field is still empty at the moment of
     *  the call. Checked against the synchronous ref, not a render's `inputs`,
     *  so a keystroke that landed while a save was in flight wins (OR-1542). */
    setInputIfEmpty: (key: InputKey, value: string) => void;
    /** Legacy blur hook — no-op now. */
    commit: (key: InputKey) => void;
    /** Replace all inputs at once (load from the saved Quote). */
    hydrate: (next: PremiumSelectionInputs) => void;
    /** Set the backend-computed totals (from a quote GET). */
    hydrateDerived: (next: PremiumSelectionDerived | null) => void;
    /** Backend-computed totals, or null before a quote is loaded. */
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
}

export function usePremiumSelection(): UsePremiumSelection {
    const [inputs, setInputs] = useState<PremiumSelectionInputs>(defaultPremiumSelectionInputs);
    const inputsRef = useRef(inputs);
    const [derived, setDerived] = useState<PremiumSelectionDerived | null>(null);

    const setInput = useCallback((key: InputKey, value: string) => {
        if (inputsRef.current[key] === value) return;
        // Keep the ref write synchronous (so `save()` / readers see current
        // data immediately), but defer the React re-render off the keystroke —
        // same policy as `useDeferredSetter`; the MoneyInput/PercentInput local
        // buffers keep typing instant.
        inputsRef.current = { ...inputsRef.current, [key]: value };
        const next = inputsRef.current;
        startTransition(() => setInputs(next));
    }, []);

    const setAndCommit = useCallback((key: InputKey, value: string) => setInput(key, value), [setInput]);
    const setInputIfEmpty = useCallback(
        (key: InputKey, value: string) => {
            if (value === "" || inputsRef.current[key].trim() !== "") return;
            setInput(key, value);
        },
        [setInput],
    );
    const commit = useCallback((_key: InputKey) => {}, []);
    const hydrate = useCallback((next: PremiumSelectionInputs) => {
        inputsRef.current = next;
        setInputs(next);
    }, []);
    const hydrateDerived = useCallback((next: PremiumSelectionDerived | null) => setDerived(next), []);

    return { inputs, setInput, setAndCommit, setInputIfEmpty, commit, hydrate, hydrateDerived, derived, pending: NO_PENDING };
}
